<template>
  <div>
    <!-- 詳細表示 -->
    <template v-if="!isEditing">
      <form-item v-bind="formDataForDisplay" />
    </template>
    <template v-else>
      <component
        :is="componentName"
        v-bind="formDataForEdit"
        @change="onChange"
      />
      <!-- 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード) 別コンポーネントで対応 -->
    </template>
  </div>
</template>

<script>
import FormItem from "./FormItem";
import FormItemDatePicker from "./FormItemDatePicker";
import FormItemTimePicker from "./FormItemTimePicker";
import FormItemSpace from "./FormItemSpace";
import FormItemDateTimePicker from "./FormItemDateTimePicker";
import FormItemSelect from "./FormItemSelect";
import FormItemRadioButtons from "./FormItemRadioButtons";
import FormItemNumber from "./FormItemNumber";
import FormItemShortText from "./FormItemShortText";
import FormItemLongText from "./FormItemLongText";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  applyFormat,
  calcDefaultValue,
  calcNextFormItemValue,
  calcEditComponentName,
} from "@/utils/operations";
import isEmpty from "lodash/isEmpty";
import { createNamespacedHelpers } from "vuex";
const { mapActions } = createNamespacedHelpers("operationReport");

const FALLBACK_VALUE = "ー";

// NOTE: 以下に対応
// 1.  年月日
// 2.  時間
// 3.  レイアウト調整用スペース
// 4.  選択項目：テキスト入力で候補絞り込みが可能であること
// 5.  ラジオボタン
// 6.  数値入力テキストエリア
// 7.  短いテキスト入力エリア
// 8.  長いテキスト入力エリア
// 9.  画像ファイルアップロード -> 別コンポーネントで対応

export default {
  name: "OperationReportShowBodyFormItem",
  components: {
    FormItem,
    FormItemDatePicker,
    FormItemTimePicker,
    FormItemSpace,
    FormItemDateTimePicker,
    FormItemSelect,
    FormItemRadioButtons,
    FormItemNumber,
    FormItemShortText,
    FormItemLongText,
  },
  props: {
    // format
    enterpriseFormVersionItem: {
      type: Object,
      required: true,
    },

    reportFormVersion: {
      type: Object,
      required: true,
    },

    // 実データ
    reportFormVersionItem: {
      type: Object,
      required: true,
    },

    fallbackValue: {
      type: String,
      default: FALLBACK_VALUE,
    },

    // 編集権限
    updatable: {
      type: Boolean,
      default: false,
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    // formの順番
    // フォームの値の更新に必要
    formVersionSortOrder: {
      type: Number,
      required: true,
    },
  },

  computed: {
    componentName() {
      const { enterpriseFormVersionItem } = this;
      const componentName = calcEditComponentName(enterpriseFormVersionItem);
      return componentName; // string
    },
    name() {
      const {
        reportFormVersion,
        reportFormVersionItem,
        enterpriseFormVersionItem,
        updatable,
      } = this;
      // 空白の場合はupdatableフラグに関係なくdisableにし、validationを通さないようにする
      const stateUpdatable =
        updatable && this.componentName !== "form-item-space"
          ? "enable"
          : "disable";
      return `operation-report-form-${stateUpdatable}_${reportFormVersion.id}_${reportFormVersionItem.id}_${enterpriseFormVersionItem.id}`; // validationに使用
    },
    // 詳細表示用データ
    formDataForDisplay() {
      const {
        enterpriseFormVersionItem,
        reportFormVersionItemValueRead: value,
        name,
        nullableLabel,
        isNullableChecked,
        isEdited,
        fallbackValue,
        hasReportFormVersionItemValue,
        unit,
        isEditing,
        disabled,
        selectOptions,
      } = this;
      const label = enterpriseFormService.getLabelFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
      const required = enterpriseFormService.getRequiredFromEnterpriseFormVersionItem(
        enterpriseFormVersionItem
      );
      return {
        label,
        required,
        name,
        value,
        unit,
        nullableLabel,
        isNullableChecked,
        isEdited,
        fallbackValue,
        hasReportFormVersionItemValue,
        isEditing,
        disabled,
        selectOptions,
      };
    },

    disabled() {
      return !this.updatable;
    },

    defaultValue() {
      const { enterpriseFormVersionItem, isEdited, disabled, isEditing } = this;
      if (disabled) return null; // 非活性ならデフォルト値を使用しない
      if (isEdited) return null; // すでに編集ずみの場合は、デフォルト値を使用しない
      if (!isEditing) return null; // 編集中でない場合は、デフォルト値を使用しない

      const defaultValue = calcDefaultValue(this, enterpriseFormVersionItem);
      return defaultValue;
    },

    // 編集表示用データ
    formDataForEdit() {
      const {
        formDataForDisplay,
        reportFormVersionItemValueEdit: initialValue,
        enterpriseFormVersionItem,
        defaultValue,
      } = this;
      return {
        ...formDataForDisplay,
        initialValue,
        enterpriseFormVersionItem,
        defaultValue,
      };
    },

    reportFormVersionItemValueRead() {
      const { reportValue: formValue, enterpriseFormVersionItem } = this;
      return applyFormat(formValue, enterpriseFormVersionItem); // 詳細の場合、フォーマットしたデータを渡す
    },

    reportFormVersionItemValueEdit() {
      const { reportValue: formValue } = this;
      return formValue; // 編集の場合formatしていないデータを渡す
    },

    hasReportFormVersionItemValue() {
      const { reportFormVersionItem } = this;
      return operationReportService.hasReportFormVersionItemValue(
        reportFormVersionItem
      );
    },

    unit() {
      return enterpriseFormService.getUnit(this.enterpriseFormVersionItem);
    },

    selectOptions() {
      return enterpriseFormService.getSelectOptions(
        this.enterpriseFormVersionItem
      ); //
    },

    isNullableChecked() {
      return operationReportService.isNullableChecked(
        this.reportFormVersionItem
      ); // boolean
    },

    nullableLabel() {
      return enterpriseFormService.getNullableLabelFromEnterpriseFormVersionItem(
        this.enterpriseFormVersionItem
      ); // string
    },

    isEdited() {
      return !isEmpty(this.reportFormVersionItem); // レポートのフォームの値がない時は、これまで編集していない
    },
    reportValue() {
      const { reportFormVersionItem } = this;
      return operationReportService.getValueFromReportFormVersionItem(
        reportFormVersionItem
      );
    },
  },

  methods: {
    ...mapActions({ setFormVersionItemValue: "setFormVersionItemValue" }),

    onChange(
      value // { value: number | string | date | [{ "name": string; }], nullableChecked }
    ) {
      const {
        formVersionSortOrder,
        enterpriseFormVersionItem,
        reportFormVersionItem,
      } = this;
      const nextFormItemValue = calcNextFormItemValue(
        enterpriseFormVersionItem,
        reportFormVersionItem,
        value
      );
      const payload = {
        formVersionSortOrder,
        ...nextFormItemValue,
      };

      this.setFormVersionItemValue(payload);
    },
  },
};
</script>
